import React, { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import Tesseract from 'tesseract.js';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { FaSpinner, FaUpload, FaTimesCircle } from "react-icons/fa";
import './Register.css';

GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default function ReuploadForm({ api, onSuccess }) {
  const [prefill, setPrefill] = useState({});
  const [editableKeys, setEditableKeys] = useState([]);
  const [vendorCode, setVendorCode] = useState('');
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [isOcrLoading, setIsOcrLoading] = useState(false);

  const fileRefs = {
    gst_file: useRef(null),
    pan_tan_file: useRef(null),
    assessment_report: useRef(null),
    nda_report: useRef(null),
    cancel_cheque: useRef(null),
    msme_certificate: useRef(null),
    passbook: useRef(null),
  };

  const labels = {
    gst_file: 'GST File',
    pan_tan_file: 'PAN File',
    assessment_report: 'Assessment Report',
    nda_report: 'NDA Report',
    cancel_cheque: 'Cancelled Cheque',
    msme_certificate: 'MSME Certificate',
    passbook: 'Passbook',
  };

  useEffect(() => {
    (async () => {
      try {
        // load reupload context
        const url = new URL(window.location.href);
        const vendor_code = url.searchParams.get('vendor_code') || '';
        const res = vendor_code
          ? await api.get('/api/vendor/reupload-context/', { params: { vendor_code } })
          : await api.get('/api/vendor/reupload-context/');
        const ctx = res.data || {};
        setPrefill(ctx.prefill || {});
        const editable = Array.isArray(ctx.editable_doc_keys) ? ctx.editable_doc_keys : [];
        setEditableKeys(editable);
        setVendorCode(ctx.vendor_code || vendor_code || '');
      } catch (e) {
        setEditableKeys([]);
        setPrefill({});
      }
    })();
  }, [api]);

  const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Validation function - same as Register.js
  const validateFileType = (file, allowXlsx = false) => {
    if (!file) return { valid: false, error: 'File is required.' };

    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      return { valid: false, error: 'File size must be less than 5 MB.' };
    }

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();

    // Blocked file types
    const blockedExtensions = ['txt', 'exe', 'bat', 'cmd', 'com', 'scr', 'vbs', 'js', 'jar', 'msi'];
    if (blockedExtensions.includes(fileExtension)) {
      return { valid: false, error: `File type .${fileExtension} is not allowed. Only JPEG, PNG, PDF${allowXlsx ? ', XLSX' : ''} files are accepted.` };
    }

    // Allowed file types
    const allowedExtensions = allowXlsx
      ? ['jpg', 'jpeg', 'png', 'pdf', 'xlsx']
      : ['jpg', 'jpeg', 'png', 'pdf'];

    if (!allowedExtensions.includes(fileExtension)) {
      return { valid: false, error: `File type .${fileExtension} is not allowed. Only JPEG, PNG, PDF${allowXlsx ? ', XLSX' : ''} files are accepted.` };
    }

    // Additional MIME type check
    const allowedMimeTypes = allowXlsx
      ? ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
      : ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

    if (!allowedMimeTypes.includes(file.type)) {
      return { valid: false, error: `Invalid file type. Only JPEG, PNG, PDF${allowXlsx ? ', XLSX' : ''} files are accepted.` };
    }

    return { valid: true };
  };

  const isMostlyEmptyText = (text) => !text || text.trim().length < 20;

  const extractPanFromText = (text) => {
    const cleaned = text.toUpperCase();
    const panRegex = /\b([A-Z]{5}[0-9]{4}[A-Z])\b/;
    const match = cleaned.match(panRegex);
    return match ? match[0] : null;
  };

  const extractPassbookInfo = (text) => {
    const rawText = String(text || "")
      .toUpperCase()
      .replace(/[\n\r\t]+/g, " ")
      .replace(/\s{2,}/g, " ")
      .replace(/[^\x20-\x7E]/g, "")
      .trim();

    const ifscRegex = /IFSC(?:\s*CODE)?\s*[:\-]?\s*([A-Z0-9]{4}[0O][A-Z0-9]{6})/i;
    let ifscMatch = rawText.match(ifscRegex);
    let ifsc = ifscMatch ? ifscMatch[1].replace(/O/g, "0") : null;

    const accRegex = /(?:A(?:C{1,2}|\/C|C\/|C\.)\s*(?:NO|N0|NUMBER)?|ACC(?:OUNT)?\s*(?:NO|N0|NUMBER)?|BANK\s*ACCOUNT\s*(?:NO|N0|NUMBER)?)\s*[:\-]?\s*([0-9]{6,20})/i;

    let accMatch = rawText.match(accRegex);
    let accountNumber = accMatch ? accMatch[1] : null;

    if (!accountNumber) {
      const fallback = rawText.match(/\b\d{9,18}\b/);
      if (fallback) accountNumber = fallback[0];
    }

    const isValid = !!ifsc && !!accountNumber;

    return {
      ifsc,
      accountNumber,
      isValid
    };
  };

  const extractMsmeNumber = (text) => {
    const cleaned = text.toUpperCase().replace(/\s+/g, " ");
    const regex = /UDYAM[\s:-]*[A-Z]{2}[-]?\d{2}[-]?\d{7}/i;
    const match = cleaned.match(regex);
    return match ? match[0].replace(/\s/g, "") : null;
  };

  const preprocessImage = async (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const scale = 2.0;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        ctx.filter = "grayscale(100%) contrast(120%) brightness(105%)";
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        resolve(canvas.toDataURL("image/png"));
      };

      reader.readAsDataURL(file);
    });
  };

  const extractTextFromPdf = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await getDocument({ data: arrayBuffer }).promise;
      let fullText = "";

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        fullText += textContent.items.map(item => item.str).join(" ") + "\n";
      }

      if (isMostlyEmptyText(fullText)) {
        const canvas = document.createElement("canvas");
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 2.0 });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport }).promise;
        const { data: { text: ocrText } } = await Tesseract.recognize(canvas.toDataURL(), 'eng');
        fullText += ocrText;
      }

      return fullText;
    } catch (err) {
      const preprocessed = await preprocessImage(file);
      const { data: { text } } = await Tesseract.recognize(preprocessed, 'eng');
      return text;
    }
  };

  const onPickFile = (key) => {
    if (fileRefs[key] && fileRefs[key].current) fileRefs[key].current.click();
  };

  const onFileChange = async (key, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type and size
    const allowXlsx = key === 'assessment_report';
    const validation = validateFileType(file, allowXlsx);
    if (!validation.valid) {
      Swal.fire({ icon: 'error', title: 'Invalid File', text: validation.error });
      e.target.value = "";
      return;
    }

    // Handle assessment report (no OCR needed)
    if (key === 'assessment_report') {
      setFiles(prev => ({ ...prev, [key]: file }));
      return;
    }

    // Handle NDA report (no OCR needed)
    if (key === 'nda_report') {
      setFiles(prev => ({ ...prev, [key]: file }));
      return;
    }

    // For other documents, run OCR
    setIsOcrLoading(true);
    const processingStart = Date.now();
    let postProcessAlert = null;
    let ocrTimeout = null;
    let ocrCompleted = false;

    try {
      let extractedText = "";

      // Create OCR processing promise
      const ocrPromise = (async () => {
        if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
          return await extractTextFromPdf(file);
        } else {
          const preprocessed = await preprocessImage(file);
          const { data: { text } } = await Tesseract.recognize(preprocessed, 'eng');
          return text;
        }
      })();

      // Create a timeout promise that rejects after 3 seconds
      const timeoutPromise = new Promise((_, reject) => {
        ocrTimeout = setTimeout(() => {
          reject(new Error('OCR_TIMEOUT'));
        }, 3000);
      });

      // Race between OCR and timeout
      try {
        extractedText = await Promise.race([ocrPromise, timeoutPromise]);
        ocrCompleted = true;
        if (ocrTimeout) clearTimeout(ocrTimeout);
      } catch (timeoutError) {
        ocrCompleted = true;
        if (ocrTimeout) clearTimeout(ocrTimeout);
        // OCR timed out - show manual entry popup
        if (timeoutError.message === 'OCR_TIMEOUT') {
          throw new Error('OCR_TIMEOUT');
        }
        throw timeoutError;
      }

      if (key === 'pan_tan_file') {
        const pan = extractPanFromText(extractedText);
        setFiles(prev => ({ ...prev, [key]: file }));
        if (!pan) {
          postProcessAlert = {
            icon: 'warning',
            title: 'PAN/TAN Not Found',
            text: 'From uploaded file, PAN number not found but we accepted your file',
            confirmButtonText: 'OK'
          };
        }
      } else if (key === 'gst_file') {
        const gstRegex = /[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]/;
        const match = extractedText.replace(/\s/g, '').toUpperCase().match(gstRegex);
        setFiles(prev => ({ ...prev, [key]: file }));
        if (!match) {
          postProcessAlert = {
            icon: 'warning',
            title: 'GST Not Found',
            text: 'Your File But File Has Been Saved With Us.',
            confirmButtonText: 'OK'
          };
        }
      } else if (key === 'passbook') {
        const info = extractPassbookInfo(extractedText);
        setFiles(prev => ({ ...prev, [key]: file }));
        if (!info.isValid) {
          postProcessAlert = {
            icon: 'warning',
            title: 'Bank Details Not Found',
            text: 'Your File But File Has Been Saved With Us.',
            confirmButtonText: 'OK'
          };
        }
      } else if (key === 'cancel_cheque') {
        const info = extractPassbookInfo(extractedText);
        setFiles(prev => ({ ...prev, [key]: file }));
        if (!info.isValid) {
          postProcessAlert = {
            icon: 'warning',
            title: 'Bank Details Not Found',
            text: 'Your File But File Has Been Saved With Us.',
            confirmButtonText: 'OK'
          };
        }
      } else if (key === 'msme_certificate') {
        const msme = extractMsmeNumber(extractedText);
        setFiles(prev => ({ ...prev, [key]: file }));
        if (!msme) {
          postProcessAlert = {
            icon: 'warning',
            title: 'MSME Not Found',
            text: 'Your File But File Has Been Saved With Us.',
            confirmButtonText: 'OK'
          };
        }
      }

    } catch (error) {
      // Handle OCR timeout or processing error
      if (error.message === 'OCR_TIMEOUT') {
        // OCR timed out after 3 seconds - show specific manual entry popup
        // Still mark file as uploaded
        setFiles(prev => ({ ...prev, [key]: file }));

        if (key === 'pan_tan_file') {
          postProcessAlert = {
            icon: 'warning',
            title: 'PAN/TAN Not Found',
            text: 'We Accpeted Your File.',
            confirmButtonText: 'OK'
          };
        } else if (key === 'gst_file') {
          postProcessAlert = {
            icon: 'warning',
            title: 'GST Not Found',
            text: 'We Accpeted Your File',
            confirmButtonText: 'OK'
          };
        } else if (key === 'passbook' || key === 'cancel_cheque') {
          postProcessAlert = {
            icon: 'warning',
            title: 'Bank Details Not Found',
            text: 'We Accpeted Your File',
            confirmButtonText: 'OK'
          };
        } else if (key === 'msme_certificate') {
          postProcessAlert = {
            icon: 'warning',
            title: 'MSME Not Found',
            text: 'We Accpeted Your File',
            confirmButtonText: 'OK'
          };
        } else {
          postProcessAlert = {
            icon: 'warning',
            title: 'Information Not Found',
            text: 'We Accpeted Your File',
            confirmButtonText: 'OK'
          };
        }
      } else {
        // Other OCR processing error - show specific message based on field
        setFiles(prev => ({ ...prev, [key]: file }));

        if (key === 'pan_tan_file') {
          postProcessAlert = {
            icon: 'warning',
            title: 'PAN/TAN Not Found',
            text: 'We Accpeted Your File',
            confirmButtonText: 'OK'
          };
        } else if (key === 'gst_file') {
          postProcessAlert = {
            icon: 'warning',
            title: 'GST Not Found',
            text: 'We Accpeted Your File',
            confirmButtonText: 'OK'
          };
        } else if (key === 'passbook' || key === 'cancel_cheque') {
          postProcessAlert = {
            icon: 'warning',
            title: 'Bank Details Not Found',
            text: 'We Accpeted Your File',
            confirmButtonText: 'OK'
          };
        } else if (key === 'msme_certificate') {
          postProcessAlert = {
            icon: 'warning',
            title: 'MSME Not Found',
            text: 'We Accpeted Your File',
            confirmButtonText: 'OK'
          };
        } else {
          postProcessAlert = {
            icon: 'error',
            title: 'Processing Error',
            text: 'Unable to process file. Please try again.',
            confirmButtonText: 'OK'
          };
        }
      }
    } finally {
      if (ocrTimeout) clearTimeout(ocrTimeout);
      setIsOcrLoading(false);
      if (postProcessAlert) {
        Swal.fire(postProcessAlert);
      }
    }
  };

  const showError = (title, text) => {
    Swal.fire({
      icon: 'error',
      title: title,
      text: text
    });
  };

  const showWarning = (title, text) => {
    Swal.fire({
      icon: 'warning',
      title: title,
      text: text
    });
  };

  const onSubmit = async () => {
    if (!vendorCode) {
      showError('Missing Vendor Code', 'Vendor code is required for re-upload.');
      return;
    }

    // Check for missing editable documents
    const missing = editableKeys.filter(k => !files[k]);
    if (missing.length > 0) {
      const missingLabels = missing.map(k => labels[k] || k);
      showError('Missing Documents', `Please upload the following required documents: ${missingLabels.join(', ')}`);
      return;
    }

    const formData = new FormData();
    formData.append('vendor_code', vendorCode);
    // append only editable files that user picked
    editableKeys.forEach(k => {
      if (files[k]) formData.append(k, files[k]);
    });
    setSubmitting(true);
    try {
      // Backend automatically sets is_active=False for all temp credentials after successful re-upload
      // This prevents further login using expired credentials
      const response = await api.post('/api/vendor/reupload-submit/', formData);

      console.log('✅ Re-upload successful - response:', response.data);

      // Additional safety check: manually deactivate temp credentials if backend didn't do it
      if (!response.data?.credentials_deactivated) {
        console.log('⚠️ Backend deactivation may have failed, calling backup endpoint...');
        try {
          const deactivateResponse = await api.post('/api/vendor/deactivate-temp-credentials/', {
            vendor_code: vendorCode
          });
          console.log('✅ Backup deactivation response:', deactivateResponse.data);
        } catch (deactivateError) {
          console.error('❌ Backup deactivation failed:', deactivateError);
          // Don't fail the  flow, just log the error
        }
      }

      // Always call deactivation as extra safety measure
      try {
        const safetyDeactivateResponse = await api.post('/api/vendor/deactivate-temp-credentials/', {
          vendor_code: vendorCode
        });
        console.log('🔒 Safety deactivation response:', safetyDeactivateResponse.data);
      } catch (safetyError) {
        console.error('❌ Safety deactivation failed:', safetyError);
        // Don't fail the main flow
      }

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Documents submitted successfully. You will be redirected to login.',
      }).then(() => {
        if (onSuccess) {
          onSuccess();
        } else {
          // Fallback: logout and redirect
          localStorage.removeItem('access');
          localStorage.removeItem('refresh');
          localStorage.removeItem('session_id');
          window.location.href = '/';
        }
      });
    } catch (e) {
      const errorMessage = e?.response?.data?.error || e?.response?.data?.detail || 'Submission failed. Please try again.';
      showError('Submission Failed', errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const readOnlyInput = (label, value) => (
    <div className="input-wrapper">
      <span className="input-label">{label}</span>
      <input type="text" value={value || ''} disabled style={{ background: '#f9fafb', cursor: 'not-allowed' }} />
    </div>
  );

  const DocRow = ({ keyName }) => {
    const picked = files[keyName]?.name;
    const enabled = editableKeys.includes(keyName);
    const isDisabled = submitting || isOcrLoading || !enabled;
    const displayValue = picked || (enabled ? 'Click to upload document' : 'Locked by approver');

    const handleDocClick = () => {
      if (!isDisabled && fileRefs[keyName] && fileRefs[keyName].current) {
        fileRefs[keyName].current.click();
      }
    };

    const handleUploadIconClick = (e) => {
      e.stopPropagation();
      if (!isDisabled && fileRefs[keyName] && fileRefs[keyName].current) {
        fileRefs[keyName].current.click();
      }
    };

    return (
      <div className="input-wrapper doc-upload-wrapper">
        <span className="input-label">{labels[keyName] || keyName}</span>
        <div
          className="input-with-icon-vendor-form doc-upload-field"
          onClick={handleDocClick}
          style={{
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            borderColor: enabled ? '#c7d2fe' : '#e5e7eb',
            background: enabled ? '#f5f7ff' : '#f3f4f6',
            opacity: isDisabled ? 0.85 : 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box'
          }}
        >
          <input
            type="text"
            value={displayValue}
            readOnly
            disabled
            style={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              background: 'transparent',
              color: enabled ? '#0f172a' : '#94a3b8',
              fontWeight: 500,
              paddingRight: picked && enabled ? '40px' : '40px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
              border: 'none',
              outline: 'none',
              margin: 0
            }}
          />
          {picked && enabled ? (
            <FaTimesCircle
              className="remove-icon"
              style={{ color: '#dc2626', cursor: 'pointer', position: 'absolute', right: '12px' }}
              onClick={(e) => {
                e.stopPropagation();
                setFiles(prev => {
                  const newFiles = { ...prev };
                  delete newFiles[keyName];
                  return newFiles;
                });
                // Reset the file input
                if (fileRefs[keyName] && fileRefs[keyName].current) {
                  fileRefs[keyName].current.value = '';
                }
              }}
            />
          ) : (
            <FaUpload
              className="upload-icon"
              style={{
                color: enabled ? '#2563eb' : '#cdd5f5',
                cursor: enabled && !isDisabled ? 'pointer' : 'not-allowed'
              }}
              onClick={handleUploadIconClick}
            />
          )}
          <input
            ref={fileRefs[keyName]}
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => onFileChange(keyName, e)}
            disabled={isDisabled}
            accept=".jpg,.jpeg,.png,.pdf,.xlsx"
          />
        </div>
      </div>
    );
  };

  // Organize documents for 2-column grid: 3 rows × 2 columns (6 docs), then 4th row with 1 doc
  // Distribute alternating: left column gets positions 0,2,4,6 and right column gets 1,3,5
  const documentKeys = Object.keys(fileRefs);

  return (
    <div style={{ padding: '40px 50px' }}>
      <div className="vendor-card-grid">
        {/* Personal Information Card */}
        <div className="vendor-card">
          <h3>Personal Information</h3>
          <div className="two-col-grid">
            {readOnlyInput('First Name', prefill.first_name)}
            {readOnlyInput('Last Name', prefill.last_name)}
            {readOnlyInput('Email', prefill.email)}
            {readOnlyInput('Phone', prefill.phone)}
            {readOnlyInput('GST Number', prefill.gst_number)}
            {readOnlyInput('PAN/TAN Number', prefill.pan_tan_number)}
            {readOnlyInput('Registered Office Address', prefill.registered_office_address)}
            {readOnlyInput('Contact Person Name', prefill.contact_person_name)}
          </div>
        </div>

        {/* Documents Card */}
        <div className="vendor-card">
          <h3>Documents</h3>
          <div className="two-col-grid">
            {documentKeys.map((key) => (
              <DocRow key={key} keyName={key} />
            ))}
          </div>
        </div>
      </div>

      <div className="vendor-actions" style={{ marginTop: 30 }}>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || isOcrLoading || editableKeys.length === 0}
          className="submit-btn"
        >
          Re-Upload
        </button>
      </div>

      {/* Full-screen loader overlay */}
      {(isOcrLoading || submitting) && (
        <div className="fullscreen-loader-overlay">
          <div className="fullscreen-loader-content">
            <FaSpinner className="spinner" style={{ fontSize: '48px', color: '#667eea' }} />
            <p style={{ marginTop: '20px', fontSize: '18px', color: '#333', fontWeight: 500 }}>
              Please Wait While We Process Your Request
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
